import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronDown,
  ExternalLink,
  AlertCircle,
  Server,
  ArrowRightLeft,
  Zap,
  LayoutDashboard,
  Monitor
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import frFlag from '../../assets/images/flags/fr.svg';
import gbFlag from '../../assets/images/flags/gb.svg';

const NavLink: React.FC<{ to: string; children: React.ReactNode; itemProp?: string }> = ({ to, children, itemProp }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${isActive
        ? 'bg-white/20 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
        : 'text-gray-300 hover:text-white hover:bg-white/10'
        }`}
      {...(itemProp ? { itemProp } : {})}
    >
      {children}
    </Link>
  );
};

const MobileNavLink: React.FC<{ to: string; children: React.ReactNode; 'aria-label'?: string }> = ({ to, children, 'aria-label': ariaLabel }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`block rounded-xl px-4 py-3 text-base font-semibold ${isActive
        ? 'bg-white/15 text-white'
        : 'text-gray-200 hover:text-white hover:bg-white/10'
        } transition`}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
};

const DropdownLink: React.FC<{
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
  disabled?: boolean;
}> = ({ to, icon, title, description, badge, disabled }) => {
  if (disabled) {
    return (
      <div className="flex items-start gap-3 rounded-xl px-3 py-2.5 opacity-60 cursor-not-allowed">
        <div className="text-gray-500 mt-0.5">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-400">{title}</span>
            {badge && (
              <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                {badge}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">{description}</div>
        </div>
      </div>
    );
  }
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition hover:bg-white/5"
    >
      <div className="text-primary mt-0.5">
        {icon}
      </div>
      <div>
        <div className="font-medium text-white">{title}</div>
        <div className="text-xs text-gray-400">{description}</div>
      </div>
    </Link>
  );
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const servicesRef = React.useRef<HTMLDivElement>(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close services dropdown on outside click
  useEffect(() => {
    if (!isServicesOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isServicesOpen]);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${isScrolled
        ? 'top-0'
        : 'top-5'
        }`}
      itemScope
      itemType="https://schema.org/WPHeader"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className={`flex items-center justify-between gap-4 rounded-2xl px-5 py-3 transition-all duration-500 ${isScrolled
          ? 'bg-[#0b0b14]/80 border border-white/[0.06] backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'bg-transparent'
        }`}>
          {/* Logo & Brand */}
          <Link to="/" className="flex items-center gap-2.5 group" aria-label={t('header.logoAria')} itemProp="url">
            <img
              src="/images/team/logo-fivemshield.png"
              alt="FIVEMSHIELD"
              className="h-8 w-8 object-contain drop-shadow-[0_0_8px_rgba(124,58,237,0.3)] transition-all duration-300 group-hover:drop-shadow-[0_0_12px_rgba(124,58,237,0.5)]"
              loading="eager"
            />
            <span className="text-lg font-bold tracking-tight" itemProp="headline">
              <span className="text-white">FIVEM</span><span className="text-white/50">SHIELD</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <motion.nav
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="hidden md:flex items-center gap-1 rounded-full px-2 py-1.5"
            itemScope
            itemType="https://schema.org/SiteNavigationElement"
          >
            <NavLink to="/" itemProp="url">
              <span itemProp="name">{t('header.nav.home')}</span>
            </NavLink>

            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1 transition ${isServicesOpen ? 'bg-white/20 text-white' : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                itemProp="name"
              >
                <span>{t('header.nav.services')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isServicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 mt-3 w-72 rounded-2xl border border-white/10 bg-[#08070f]/95 backdrop-blur-xl shadow-[0_25px_80px_rgba(0,0,0,0.55)] p-4 z-50"
                  >
                    <DropdownLink
                      to="/services/vps"
                      icon={<Server className="w-5 h-5" />}
                      title={t('header.services.vps.title')}
                      description={t('header.services.vps.desc')}
                    />
                    <DropdownLink
                      to="/services/vps-windows"
                      icon={<Monitor className="w-5 h-5" />}
                      title={t('header.services.vpsWindows.title')}
                      description={t('header.services.vpsWindows.desc')}
                    />
                    <DropdownLink
                      to="/services/reverse-proxy"
                      icon={<ArrowRightLeft className="w-5 h-5" />}
                      title={t('header.services.proxy.title')}
                      description={t('header.services.proxy.desc')}
                    />
                    <DropdownLink
                      to="/services/cdn-caching"
                      icon={<Zap className="w-5 h-5" />}
                      title={t('header.services.cdn.title')}
                      description={t('header.services.cdn.desc')}
                    />
                    <DropdownLink
                      to="/services/game-panel"
                      icon={<LayoutDashboard className="w-5 h-5" />}
                      title={t('header.services.panel.title')}
                      description={t('header.services.panel.desc')}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink to="/about" itemProp="url">
              <span itemProp="name">{t('header.nav.about')}</span>
            </NavLink>
            <NavLink to="/cfx-finder" itemProp="url">
              <span itemProp="name">{t('header.nav.cfxFinder')}</span>
            </NavLink>
            <NavLink to="/blog" itemProp="url">
              <span itemProp="name">{t('header.nav.blog')}</span>
            </NavLink>
            <NavLink to="/contact" itemProp="url">
              <span itemProp="name">{t('header.nav.contact')}</span>
            </NavLink>

            <div className="flex items-center gap-2 ml-2 whitespace-nowrap">
              <a
                href="https://stats.fivemshield.net"
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-gray-200 transition hover:border-primary/60 hover:text-white"
              >
                {t('header.nav.protection')}
                <ExternalLink className="w-4 h-4" />
              </a>

              <a
                href="https://manager.fivemshield.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-primary to-primary-light px-5 py-2 text-sm font-semibold text-white shadow-[0_10px_35px_rgba(124,58,237,0.45)] transition hover:shadow-[0_15px_45px_rgba(124,58,237,0.55)]"
              >
                {t('header.nav.login')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Language Switch (flags) */}
            <div className="ml-3 flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-1">
              <button
                onClick={() => i18n.changeLanguage('fr')}
                className={`rounded-full px-2 py-1 text-xs font-semibold ${i18n.language?.startsWith('fr') ? 'bg-white/20 text-white' : 'text-gray-200 hover:bg-white/10'
                  }`}
                aria-label="Français"
                title="Français"
              >
                <img src={frFlag} alt="Français" className="w-5 h-4 object-cover" />
              </button>
              <button
                onClick={() => i18n.changeLanguage('en')}
                className={`rounded-full px-2 py-1 text-xs font-semibold ${i18n.language?.startsWith('en') ? 'bg-white/20 text-white' : 'text-gray-200 hover:bg-white/10'
                  }`}
                aria-label="English"
                title="English"
              >
                <img src={gbFlag} alt="English" className="w-5 h-4 object-cover" />
              </button>
            </div>
          </motion.nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-full border border-white/15 bg-white/5 text-white backdrop-blur"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t border-white/10 bg-[#05050b]/95 backdrop-blur-xl overflow-hidden"
          >
            <div className="px-6 py-4 space-y-4">
              <MobileNavLink to="/">{t('header.nav.home')}</MobileNavLink>

              {/* Mobile Services dropdown */}
              <div className="space-y-4">
                <span className="text-gray-400 text-sm font-medium px-4">{t('header.nav.services')}</span>
                <div className="space-y-2 pl-4">
                  <MobileNavLink to="/services/vps" aria-label="Hébergement VPS FiveM">{t('header.services.vps.title')}</MobileNavLink>
                  <MobileNavLink to="/services/vps-windows" aria-label="VPS Windows">{t('header.services.vpsWindows.title')}</MobileNavLink>
                  <MobileNavLink to="/services/reverse-proxy" aria-label="Protection Anti-DDoS pour FiveM">{t('header.services.proxy.title')}</MobileNavLink>
                  <MobileNavLink to="/services/cdn-caching" aria-label="CDN & Caching FiveM">{t('header.services.cdn.title')}</MobileNavLink>
                  <MobileNavLink to="/services/game-panel" aria-label="Game Panel FiveM">{t('header.services.panel.title')}</MobileNavLink>
                </div>
              </div>

              <MobileNavLink to="/about" aria-label="À propos">
                {t('header.nav.about')}
              </MobileNavLink>

              <MobileNavLink to="/cfx-finder" aria-label="CFX Finder">
                {t('header.nav.cfxFinder')}
              </MobileNavLink>

              <MobileNavLink to="/blog" aria-label="Blog">
                {t('header.nav.blog')}
              </MobileNavLink>

              <a
                href="https://stats.fivemshield.net"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-2 px-4 text-lg border-l-2 border-transparent text-white hover:text-primary transition-colors duration-200"
              >
                {t('header.nav.protection')}
                <ExternalLink className="inline-block ml-1 w-4 h-4" />
              </a>

              <a
                href="https://manager.fivemshield.net"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-2 px-4 text-lg border-l-2 border-transparent text-white hover:text-primary transition-colors duration-200"
              >
                {t('header.nav.login')}
                <ExternalLink className="inline-block ml-1 w-4 h-4" />
              </a>

              <MobileNavLink to="/contact">{t('header.nav.contact')}</MobileNavLink>

              <div className="pt-4">
                <Link
                  to="/contact"
                  className="block w-full bg-gradient-to-r from-primary to-primary-light px-6 py-3 rounded-md font-medium text-white text-center"
                >
                  {t('header.mobile.getProtection')}
                </Link>
              </div>

              <div className="flex items-center space-x-2 pt-4 border-t border-gray-800">
                <AlertCircle className="w-5 h-5 text-primary" />
                <span className="text-gray-300 text-sm">{t('header.mobile.support')}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;